import {SpringPageable} from "@variocube/app-ui";
import {useMemo} from "react";
import {TypeOf} from "zod";
import {useAccessToken} from "../auth";
import {useClientId} from "../ClientIdProvider";
import {useTenantId} from "../tenant";
import {createApiClientWithBaseConfig, PasswordReset, TenantUserCreation, UserMutation} from "./client";
import {get_QueryUsers, UserImportRequest} from "./client.gen";

export function useUserApi() {
	const accessToken = useAccessToken();
	const clientId = useClientId();
	return useMemo(() => createUserApi(clientId, accessToken), [accessToken]);
}

export function useTenantUserApi() {
	const accessToken = useAccessToken();
	const tenantId = useTenantId();
	const clientId = useClientId();
	return useMemo(() => createTenantUserApi(tenantId, clientId, accessToken), [tenantId, accessToken]);
}

export type UserQuery = TypeOf<get_QueryUsers["parameters"]>["query"];

function createUserApi(clientId: string | undefined, accessToken: string | undefined) {
	const {api, withConfig} = createApiClientWithBaseConfig(clientId, accessToken);
	return {
		getCurrentUser: () => api.get("/api/user", withConfig({})),
		findUserByEmail: (email: string) =>
			api.get(
				"/api/users/{email}",
				withConfig({
					path: {email},
				}),
			),
	};
}

function createTenantUserApi(tenantId: string, clientId: string | undefined, accessToken: string | undefined) {
	const {api, withConfig} = createApiClientWithBaseConfig(clientId, accessToken);
	return {
		getUser: (username: string) =>
			api.get(
				"/api/tenants/{tenantId}/users/{username}",
				withConfig({
					path: {tenantId, username},
				}),
			),
		createUser: (creation: TenantUserCreation) =>
			api.post(
				"/api/tenants/{tenantId}/users",
				withConfig({
					path: {tenantId},
					body: creation,
				}),
			),
		addExistingUser: (username: string) =>
			api.post(
				"/api/tenants/{tenantId}/users/{username}",
				withConfig({
					path: {tenantId, username},
				}),
			),
		updateUser: (username: string, mutation: UserMutation) =>
			api.put(
				"/api/tenants/{tenantId}/users/{username}",
				withConfig({
					path: {tenantId, username},
					body: mutation,
				}),
			),
		deleteUser: (username: string) =>
			api.delete(
				"/api/tenants/{tenantId}/users/{username}",
				withConfig({
					path: {tenantId, username},
				}),
			),
		resetPassword: (username: string, passwordReset: PasswordReset) =>
			api.put(
				"/api/tenants/{tenantId}/users/{username}/password",
				withConfig({
					path: {tenantId, username},
					body: passwordReset,
				}),
			),
		queryUsers: (query: UserQuery) =>
			api.get(
				"/api/tenants/{tenantId}/users",
				withConfig({
					path: {tenantId},
					query,
				}),
			),
		getUserBookings: (username: string, pageable: SpringPageable) =>
			api.get(
				"/api/tenants/{tenantId}/users/{username}/bookings",
				withConfig({
					path: {tenantId, username},
					query: pageable,
				}),
			),
		getUserGroups: (username: string) =>
			api.get(
				"/api/tenants/{tenantId}/users/{username}/groups",
				withConfig({
					path: {tenantId, username},
				}),
			),
		getUserAuditLog: (username: string, pageable: SpringPageable) =>
			api.get(
				"/api/tenants/{tenantId}/users/{username}/audit",
				withConfig({
					path: {tenantId, username},
					query: pageable,
				}),
			),
		removeUserFromGroup: (username: string, groupId: number) =>
			api.delete(
				"/api/tenants/{tenantId}/users/{username}/groups/{groupId}",
				withConfig({
					path: {tenantId, username, groupId},
				}),
			),
		addUserToGroup: (username: string, groupId: number) =>
			api.post(
				"/api/tenants/{tenantId}/users/{username}/groups/{groupId}",
				withConfig({
					path: {tenantId, username, groupId},
				}),
			),
		importUsers: (request: UserImportRequest) =>
			api.post(
				"/api/tenants/{tenantId}/users/import",
				withConfig({
					path: {tenantId},
					body: request,
				}),
			),
	};
}
